const resources = [
  {
    title: "Tampa International Airport (TPA)",
    url: "https://www.flytpa.com",
    description: "Clearwater's primary international gateway with direct flights to London, Frankfurt, Zurich, Cancun, and Toronto. Check flight status, parking, and security wait times.",
    icon: "ri-plane-line",
  },
  {
    title: "St. Pete-Clearwater International Airport (PIE)",
    url: "https://www.fly2pie.com",
    description: "Allegiant Air's major hub just 10 miles south of Clearwater. Domestic low-cost connections to international gateway cities. No customs facilities — international travel requires TPA.",
    icon: "ri-flight-takeoff-line",
  },
  {
    title: "U.S. Department of State — Travel.State.Gov",
    url: "https://travel.state.gov",
    description: "Official passport application forms (DS-11, DS-82, DS-64, DS-3053), processing time estimates, entry requirements by country, and travel advisories for Clearwater international travelers.",
    icon: "ri-government-line",
  },
  {
    title: "REAL ID Requirements",
    url: "https://www.dhs.gov/real-id",
    description: "Florida began issuing REAL ID-compliant licenses in 2010. If your Florida driver's license has a star in the upper right corner, it is REAL ID compliant. Required for domestic flights from TPA and PIE starting May 7, 2025.",
    icon: "ri-shield-check-line",
  },
  {
    title: "Pinellas County Clerk of the Circuit Court",
    url: "https://www.mypinellasclerk.org",
    description: "Passport acceptance services by appointment at the Clearwater courthouse, plus marriage licenses, notary services, and official records for name-change passport updates.",
    icon: "ri-building-line",
  },
  {
    title: "Philadelphia Phillies Spring Training — Spectrum Field",
    url: "https://www.mlb.com/phillies/spring-training",
    description: "Schedule, tickets, and fan information for Phillies Grapefruit League games in downtown Clearwater. Many fans extend spring training into international travel — check your passport early.",
    icon: "ri-flag-line",
  },
];

export default function ClearwaterHelpfulResourcesSection() {
  return (
    <section id="local-resources" className="py-16 md:py-20 bg-[#F8F9FB]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <span className="inline-block text-[#B22222] text-xs font-bold uppercase tracking-[0.15em] mb-3">
            Helpful Links
          </span>
          <h2 className="font-serif text-2xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Resources for Clearwater International Travelers
          </h2>
          <p className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            Official links for Pinellas County residents planning international flights from TPA,
            Allegiant connections from PIE, spring training travel, and cruise departures from Tampa.
          </p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-file-text-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Vital Records</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Birth, Death, Marriage &amp; Divorce Records — Pinellas County</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-600 text-sm leading-relaxed mb-5">
                If you need a certified copy of your birth certificate for a passport application, a marriage certificate for a name change, or a divorce decree to support your passport correction, here are the official sources for vital records in Pinellas County. Most records can be ordered online for delivery within 5–10 business days.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                {[
                  { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the Florida Department of Health in Pinellas County. Online, mail, or in-person options available.", url: "https://pinellas.floridahealth.gov/" },
                  { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through the Florida Department of Health in Pinellas County. Required for estate and legal matters.", url: "https://pinellas.floridahealth.gov/" },
                  { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Pinellas County Clerk of Courts. Certified copies available for name changes and legal purposes.", url: "https://www.pinellasclerk.org/" },
                  { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Pinellas County Clerk of Courts. Required for name change passport corrections and legal documentation.", url: "https://www.pinellasclerk.org/" },
                ].map((record, i) => (
                  <a
                    key={i}
                    href={record.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3.5 bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 mt-0.5">
                      <i className={`${record.icon} text-[#1A365D] text-base group-hover:text-[#F6C344] transition-colors`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{record.label}</p>
                      <p className="text-gray-600 text-xs leading-relaxed mb-1.5">{record.desc}</p>
                      <span className="inline-flex items-center gap-1 text-[#1A365D] font-semibold text-xs group-hover:text-[#F6C344] transition-colors">
                        <i className="ri-external-link-line text-xs"></i>Get Records
                      </span>
                    </div>
                  </a>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-information-line text-amber-500 mr-1.5"></i>
                  <strong>Don&apos;t see your county?</strong> If the record you need was issued outside Pinellas County, visit <a href="https://www.vitalchek.com/" target="_blank" rel="noopener noreferrer" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">VitalChek.com</a> — the official online service for ordering vital records nationwide across all U.S. states and counties.
                </p>
              </div>
            </div>
          </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {resources.map((r, i) => (
            <a
              key={i}
              href={r.url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-lg p-6 border border-gray-100 hover:border-[#F6C344]/50 transition-all group block"
            >
              <div className="w-10 h-10 rounded-lg bg-[#1A365D] flex items-center justify-center mb-4 group-hover:bg-[#F6C344] transition-colors">
                <i className={`${r.icon} text-white group-hover:text-[#1A365D] transition-colors text-lg`}></i>
              </div>
              <h3 className="font-serif text-sm font-bold text-[#1A365D] mb-2 group-hover:text-[#B22222] transition-colors">
                {r.title}
              </h3>
              <p className="text-gray-600 text-xs leading-relaxed mb-3">{r.description}</p>
              <div className="flex items-center gap-1 text-[#F6C344] text-xs font-semibold">
                <span>Visit Website</span>
                <i className="ri-arrow-right-up-line"></i>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}